import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { ErrorState } from '../ui/error-state'
import { Input } from '../ui/input'
import { LoadingTable } from '../ui/loading-states'
import { Pagination } from '../ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { usePharmaTurnover } from '../../hooks/usePharmaAnalytics'
import {
  formatCurrency,
  formatIsoDate,
  formatNumber,
} from '../../utils/dateFormat'
import type { PharmaTurnoverFilters } from '../../types/pharma-analytics.types'

interface TurnoverTabProps {
  filters: PharmaTurnoverFilters
  onFiltersChange: (
    update: (prev: PharmaTurnoverFilters) => PharmaTurnoverFilters
  ) => void
}

const OPERATION_LABELS: Record<string, string> = {
  purchase: 'Закупка',
  writeoff: 'Списание',
  transfer_in: 'Перемещение (приход)',
  transfer_out: 'Перемещение (расход)',
  surplus: 'Оприходование излишков',
  return: 'Возврат',
}

const CHART_COLORS = ['#0ea5e9', '#f59e0b', '#94a3b8', '#a3a3a3']

export function TurnoverTab({ filters, onFiltersChange }: TurnoverTabProps) {
  const { data, isLoading, error, refetch } = usePharmaTurnover(filters)

  const rows = data?.rows ?? []
  const limit = filters.limit ?? 50
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / limit))

  // Столбики строятся по внешнему обороту: перемещения между складами — это один
  // и тот же товар с двух сторон, и в диаграмме они удвоили бы картину.
  const chartData = rows
    .slice(0, 12)
    .map((row) => ({
      name: (row.warehouseName ?? row.warehouseCode).replace('ЛСиИМН ', ''),
      Закупки: row.totals.externalIn.cost ?? 0,
      Списания: row.totals.externalOut.cost ?? 0,
    }))
    .filter((row) => row.Закупки > 0 || row.Списания > 0)

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Оборот и закупки по складам</CardTitle>
          <CardDescription>
            Движения регистра «Учет медикаментов» за период. Внутренние перемещения
            показаны отдельно и во внешний оборот не входят.
            {data?.dataThrough && (
              <> Данные о движениях — по {formatIsoDate(data.dataThrough)}.</>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Начало периода</label>
              <Input
                type="date"
                className="w-40"
                value={filters.from ?? data?.period.from ?? ''}
                onChange={(e) =>
                  onFiltersChange((prev) => ({
                    ...prev,
                    from: e.target.value || undefined,
                    page: 1,
                  }))
                }
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Конец периода</label>
              <Input
                type="date"
                className="w-40"
                value={filters.to ?? data?.period.to ?? ''}
                onChange={(e) =>
                  onFiltersChange((prev) => ({
                    ...prev,
                    to: e.target.value || undefined,
                    page: 1,
                  }))
                }
              />
            </div>
          </div>

          {data && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <SummaryTile
                title="Приход извне"
                hint="закупки, излишки, возвраты"
                value={formatCurrency(data.totals.externalIn.cost, { compact: true })}
                sub={`${formatNumber(data.totals.externalIn.rows)} строк`}
              />
              <SummaryTile
                title="Списано"
                hint="реальный отток со складов"
                value={formatCurrency(data.totals.externalOut.cost, { compact: true })}
                sub={`${formatNumber(data.totals.externalOut.rows)} строк`}
              />
              <SummaryTile
                title="Перемещения"
                hint="между складами, в оборот не входят"
                value={formatCurrency(data.totals.internalIn.cost, { compact: true })}
                sub={
                  data.totals.internalNetMismatch === 0
                    ? 'приход и расход сошлись'
                    : `расхождение ${formatNumber(data.totals.internalNetMismatch, { maximumFractionDigits: 3 })}`
                }
                tone={data.totals.internalNetMismatch === 0 ? 'default' : 'warn'}
              />
              <SummaryTile
                title="Строк без цены"
                hint="стоимость по ним неизвестна"
                value={formatNumber(data.totals.rowsWithoutCost)}
                sub="цена не разобралась из партии"
              />
            </div>
          )}

          {chartData.length > 0 && (
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 8, right: 8, left: 8, bottom: 56 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    dataKey="name"
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                    height={70}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: number) =>
                      formatCurrency(v, { compact: true, fallback: '0' })
                    }
                    width={80}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(Number(value))}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Закупки" fill={CHART_COLORS[0]} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Списания" fill={CHART_COLORS[1]} radius={[3, 3, 0, 0]}>
                    {chartData.map((entry) => (
                      <Cell key={entry.name} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {isLoading ? (
            <LoadingTable rows={8} columns={5} />
          ) : error ? (
            <ErrorState
              title="Не удалось загрузить оборот"
              description={(error as Error)?.message}
              onAction={() => void refetch()}
            />
          ) : rows.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              За выбранный период движений нет
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Склад</TableHead>
                      <TableHead className="text-right">Закупки</TableHead>
                      <TableHead className="text-right">Списания</TableHead>
                      <TableHead className="text-right">Перемещения (приход)</TableHead>
                      <TableHead className="text-right">Перемещения (расход)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.warehouseCode}>
                        <TableCell className="font-medium max-w-xs">
                          <div className="truncate" title={row.warehouseName ?? ''}>
                            {row.warehouseName ?? row.warehouseCode}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(row.totals.externalIn.cost)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(row.totals.externalOut.cost)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {formatCurrency(row.totals.internalIn.cost)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {formatCurrency(row.totals.internalOut.cost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Pagination
                currentPage={filters.page ?? 1}
                totalPages={totalPages}
                onPageChange={(page) =>
                  onFiltersChange((prev) => ({ ...prev, page }))
                }
              />
            </>
          )}

          {data && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
              {Object.entries(data.totals.operations).map(([type, totals]) => (
                <span key={type}>
                  {OPERATION_LABELS[type] ?? type}:{' '}
                  <span className="tabular-nums font-medium text-foreground">
                    {formatCurrency(totals.cost, { compact: true })}
                  </span>{' '}
                  ({formatNumber(totals.rows)} строк)
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function SummaryTile({
  title,
  hint,
  value,
  sub,
  tone = 'default',
}: {
  title: string
  hint: string
  value: string
  sub: string
  tone?: 'default' | 'warn'
}) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs text-muted-foreground">{title}</div>
      <div
        className={`text-xl font-bold tabular-nums mt-0.5 ${
          tone === 'warn' ? 'text-amber-600 dark:text-amber-500' : ''
        }`}
      >
        {value}
      </div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
      <div className="text-[11px] text-muted-foreground/70">{hint}</div>
    </div>
  )
}
